import { getFooterTemplate, getProjectInfo } from 'global-utils/get-attributes';
import { getPreferredLang } from 'global-utils/get-language';
import { getPoliciesUrl } from 'global-utils/get-policies-url';
import { globalScope } from 'global-utils/globals';
import { initGF } from './index';
import { FooterTemplate } from './types';
import { FOOTER_LABEL_FALLBACKS } from './utils/build-labels';
import { buildGlobalFooterData } from './utils/build-global-footer-data';

const HYDRATION_RESET_CHECK_DELAYS = [100, 500, 1500, 5000];

const initialiseGlobalFooterData = () => {
  const { prjMarket, projectId } = getProjectInfo();
  const language = getPreferredLang();
  const links = getPoliciesUrl(language, prjMarket, projectId);

  globalScope.global_footer = buildGlobalFooterData(
    links,
    FOOTER_LABEL_FALLBACKS
  );
};

const hasFooterReverted = () => {
  const container = document.getElementById('cbg-global-footer');

  if (!container) {
    return false;
  }

  const parent = container.parentNode;
  const containerIsPristine = container.dataset.cbgGfInitialized !== 'true';
  const parentLostMarker =
    parent instanceof Element &&
    !parent.hasAttribute('data-cbg-gf-initialized');

  return containerIsPristine || parentLostMarker;
};

const reinitFooterIfReverted = (footerTemplate: FooterTemplate) => {
  if (!hasFooterReverted()) {
    return;
  }

  const container = document.getElementById('cbg-global-footer');

  if (container) {
    initGF(footerTemplate, container);
  }
};

const scheduleHydrationResetChecks = (footerTemplate: FooterTemplate) => {
  const check = () => reinitFooterIfReverted(footerTemplate);

  requestAnimationFrame(() => {
    requestAnimationFrame(check);
  });

  HYDRATION_RESET_CHECK_DELAYS.forEach((delay) => setTimeout(check, delay));
};

export const initGlobalFooter = () => {
  const hasTemplateAttribute = document
    .getElementById('cbgCompPack')
    ?.hasAttribute('data-footer-template');
  const configuredTemplate = getFooterTemplate();

  if (hasTemplateAttribute && !configuredTemplate) {
    return;
  }

  const footerTemplate: FooterTemplate = configuredTemplate || [
    { tag: 'li' },
    { tag: 'a' }
  ];

  initialiseGlobalFooterData();

  const mountFooter = () => {
    const container = document.getElementById('cbg-global-footer');

    if (!container) {
      if (configuredTemplate) {
        console.error(
          'cbgGlobalFooter:: data-footer-template is set but no element with id="cbg-global-footer" was found in the DOM'
        );
      }
      return;
    }

    const parent = container.parentNode;

    if (
      parent instanceof Element &&
      parent.hasAttribute('data-cbg-gf-initialized')
    ) {
      return;
    }

    if (!hasTemplateAttribute) {
      console.warn(
        'cbgGlobalFooter:: To style the footer, set data-footer-template on the cbgCompPack script. See https://ottilia.carlsberggroup.com/plugin-docs/global-footer#footer-configuration for details.'
      );
    }

    initGF(footerTemplate, container);
    scheduleHydrationResetChecks(footerTemplate);
  };

  if (
    document.readyState === 'interactive' ||
    document.readyState === 'complete'
  ) {
    mountFooter();
  } else {
    document.addEventListener('DOMContentLoaded', mountFooter);
  }
};
